import './index.less';
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from 'chart.js';
import { Bar, Pie } from 'react-chartjs-2';
import { Card, Col, DatePicker, Row, Select, Spin, Table, Typography, Button } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { useHistory } from 'react-router-dom';
import isBetween from 'dayjs/plugin/isBetween';
import ChartDataLabels from 'chartjs-plugin-datalabels';

import {
  contractAreas,
  contractClasification,
  contractMonths,
  contractStates,
  contractClasificationByArea,
  filterByTipeAndValue,
} from './utils/Functions';

import RequestServices from '../../../../domain/usecases/RequestServices';
import UserServices from '../../../../domain/usecases/UserServices';
import { Routes } from '../../../configuration/routes/Routes';
import { columnsTable, states } from './utils/Constants';
import { pathServer, ROL_CODE, areaTypes } from '../../../utilities/Constant';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  ChartDataLabels
);

const { Title: AntTitle, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;
dayjs.extend(isBetween);

export default function HomeAdminTemplate({ name }) {
  const [isLoading, setIsLoading] = useState(false);
  const [requests, setRequests] = useState([]);
  const [listLegalOwner, setListLegalOwner] = useState([]);
  const [requestsFinalized, setRequestsFinalized] = useState([]);

  const [hasError, setHasError] = useState(null);
  const [selectedState, setSelectedState] = useState('Todos');
  const [selectedArea, setSelectedArea] = useState('Todos');
  const [selectedResposable, setSelectedResposable] = useState('Todos');
  const [selectedWeek, setSelectedWeek] = useState('');

  const [dateRange, setDateRange] = useState(null);

  const [showViewDetails, setShowViewDetails] = useState(false);
  const [optionsDetail, setOptionsDetail] = useState({
    option: '',
    value: '',
    isFirstSection: true,
  });
  const history = useHistory();

  const listWeeks = [
    { value: 1, label: '1 semana' },
    { value: 2, label: '2 semanas' },
    { value: 3, label: '3 semanas' },
    { value: 4, label: '4 semanas' },
    { value: 10000000, label: '+ 4 semanas' },
  ];

  // Filtrar contratos por estado y rango de fechas
  const filteredContracts = requests.filter((contract) => {
    const matchesState = selectedState === 'Todos' || contract.state === selectedState;
    const matchesArea = selectedArea === 'Todos' || contract.userArea === selectedArea;

    const matchesDate = dateRange
      ? dayjs(contract.contractDateCalendar).isBetween(dateRange[0], dateRange[1], 'day', '[]')
      : true;
    return matchesState && matchesDate && matchesArea;
  });

  // Filtrar contratos por estado y rango de fechas
  const filteredContractsFinalized = requestsFinalized.filter((requestFinalized) => {
    const matchesResponsable =
      selectedResposable === 'Todos' || requestFinalized.legalOwner === selectedResposable;

    const currentDate = new Date().getTime();

    const matchesWeekAgo =
      selectedWeek === '' ||
      currentDate + selectedWeek * 6048000000 - requestFinalized.expirationDateTimestamp * 1000 >=
        0;
    return matchesResponsable && matchesWeekAgo;
  });

  const [expired, upcoming, current] = contractClasification([...filteredContractsFinalized]);

  const goRequestList = () => {
    history.push(Routes.REQUEST.LIST);
  };

  // Datos para el gráfico de estado de contratos

  const stateChartData = {
    labels: Object.keys(contractStates([...filteredContracts])),
    datasets: [
      {
        label: 'Contratos',
        data: Object.values(contractStates([...filteredContracts])),
        backgroundColor: [
          '#008ccc',
          '#2fb182',
          '#003171',
          '#f99625',
          '#e60d2e',
          '#791697',
          '#5f6368',
          '#1890ff',
          '#52c41a',
        ],
      },
    ],
  };
  const stateChartOptions = {
    responsive: true,
    plugins: {
      tooltip: {
        callbacks: {
          label(context) {
            const value = context.raw;
            const { data } = context.dataset;
            const total = data.reduce((sum, val) => sum + val, 0);
            const percentage = ((value / total) * 100).toFixed(2);
            return `${context.label}: ${value} (${percentage}%)`;
          },
        },
      },
      legend: {
        position: 'bottom',
      },
      datalabels: {
        color: 'white',
        anchor: 'center',
        align: 'center',
        font: {
          weight: 'bold',
          size: 10,
        },
        formatter: (value) => value,
      },
    },
  };

  const onChartClick = (event, elements, chartType, isFirstSection = true) => {
    if (!elements.length) return;
    setShowViewDetails(true);
    const chart = elements[0];
    const labelIndex = chart.index;
    const selectedLabel = stateChartData.labels[labelIndex];
    setOptionsDetail({ option: chartType, value: selectedLabel, isFirstSection });
  };

  // Datos para el gráfico de contratos por área)
  const areaChartData = {
    labels: Object.keys(contractAreas([...filteredContracts])),
    datasets: [
      {
        label: 'Contratos por Área',
        data: Object.values(contractAreas([...filteredContracts])),
        backgroundColor: ['#1890ff', '#fa541c', '#f99625'],
      },
    ],
  };

  // Datos para el gráfico de contratos por mes
  const monthChartData = {
    labels: Object.keys(contractMonths([...filteredContracts])),
    datasets: [
      {
        label: 'Contratos por Mes',
        data: Object.values(contractMonths([...filteredContracts])),
        backgroundColor: '#1890ff',
      },
    ],
  };

  // Datos para el gráfico de contratos próximos a vencer
  const upcomingContractsData = {
    labels: ['Próximos a Vencer', 'Vencidos', 'Vigentes'],
    datasets: [
      {
        label: 'Contratos',
        data: [upcoming, expired, current],
        backgroundColor: ['#faad14', '#ff4d4f', '#1890ff'],
      },
    ],
  };

  // Convertir datos para el gráfico
  const classifiedData = contractClasificationByArea([...filteredContractsFinalized]);

  const areas = Object.keys(classifiedData);

  const expiredData = areas.map((area) => classifiedData[area].expired);
  const upcomingData = areas.map((area) => classifiedData[area].upcoming);
  const currentData = areas.map((area) => classifiedData[area].current);

  const dataChart = {
    labels: areas,
    datasets: [
      {
        label: 'Vencidos',
        data: expiredData,
        backgroundColor: '#e74c3c',
        stack: 'estado',
      },
      {
        label: 'Próximos a Vencer',
        data: upcomingData,
        backgroundColor: '#f1c40f',
        stack: 'estado',
      },
      {
        label: 'Vigentes',
        data: currentData,
        backgroundColor: '#1890ff',
        stack: 'estado',
      },
    ],
  };

  const optionsChart = {
    responsive: true,
    plugins: {
      title: {
        display: true,
        text: 'Contratos por área y estado',
      },
      legend: {
        position: 'top',
      },
      datalabels: {
        display: false,
      },
      tooltip: {
        mode: 'index',
        intersect: false,
      },
    },
    scales: {
      x: {
        stacked: true,
        ticks: {
          autoSkip: false, // Evita que se salten etiquetas si son muchas
          maxRotation: 45,
          minRotation: 45,
        },
      },
      y: {
        stacked: true,
        beginAtZero: true,
      },
    },
  };

  const getLegalOwnerList = useCallback(async () => {
    const [data, error] = await UserServices.getUsersByRolId(ROL_CODE.legalOwner);
    if (data && error === null) {
      const optionsLegalOwner = data.map((item) => ({
        value: item.email,
        label: `${item.firstName} ${item.fatherLastName}`,
      }));
      setListLegalOwner([...optionsLegalOwner]);
    }
  }, []);

  const getRequestList = useCallback(async () => {
    setIsLoading(true);
    const [data, error] = await RequestServices.getRequestList();
    if (data && error === null) {
      const dataUpdate = data.map((item) => ({ ...item, state: states[item.state] }));
      setRequests(dataUpdate.sort((a, b) => b.updateDate - a.updateDate));
      setRequestsFinalized(
        dataUpdate
          .filter((item) => item.state === 'Finalizado')
          .sort((a, b) => b.updateDate - a.updateDate)
      );
    } else {
      setHasError(error);
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    getRequestList();
  }, [getRequestList]);

  useEffect(() => {
    getLegalOwnerList();
  }, [getLegalOwnerList]);

  if (isLoading)
    return (
      <Spin
        spinning={isLoading}
        tip="Cargando información..."
        style={{ height: '500px', maxHeight: '500px', margin: 'auto', width: '100%' }}
      />
    );
  if (hasError) return `Ocurrio un error al cargar la información`;

  return (
    <div className="homeAdmin-container">
      {!showViewDetails ? (
        <>
          <Row gutter={16}>
            <Col span={16}>
              <AntTitle className="home-title">{`¡Bienvenido de nuevo,${name}!`}</AntTitle>
              <div className="header-container">
                <Text className="home-subtitle">Hagamos lo mejor hoy</Text>
                <img src={`${pathServer.PATH_IMG}ic_welcome.png`} />
              </div>
            </Col>

            {/* <Col span={8}>
          <Calendar type={1} />
        </Col> */}
          </Row>
          <hr />
          <div className="main-container">
            <div style={{ marginBottom: '20px' }}>
              <Select
                defaultValue="Todos"
                onChange={setSelectedState}
                style={{ width: 200, marginRight: 10 }}
              >
                <Option value="Todos">Todos (Estado)</Option>
                <Option value="Nuevo">Nuevo</Option>
                <Option value="Revision">Revision</Option>
                <Option value="Proceso">Proceso</Option>
                <Option value="Observado">Observado</Option>
                <Option value="Finalizado">Finalizado</Option>
                <Option value="Cancelado">Cancelado</Option>
              </Select>
              <Select
                defaultValue="Todos"
                onChange={setSelectedArea}
                style={{ width: 200, marginRight: 10 }}
              >
                <Option value="Todos">Todos (Área usuaria)</Option>

                {areaTypes.map((item) => (
                  <Option value={item.label} key={item.value}>
                    {item.label}
                  </Option>
                ))}

                <Option value="Finanzas">Finanzas</Option>
                <Option value="Legal">Legal</Option>
                <Option value="Sistemas">Sistemas</Option>
              </Select>

              <RangePicker onChange={setDateRange} />
            </div>
            <div className="container-graphics">
              <Row gutter={16}>
                <Col span={12}>
                  <Card title="Estado de Contratos">
                    <Pie
                      data={stateChartData}
                      options={{
                        ...stateChartOptions,
                        onClick: (event, elements) => onChartClick(event, elements, 'state'),
                      }}
                    />
                  </Card>
                </Col>
                <Col span={12}>
                  <Card title="Contratos por Área Solicitante">
                    <Bar
                      data={areaChartData}
                      options={{
                        ...stateChartOptions,
                        onClick: (event, elements) => {
                          if (elements.length === 0) return;
                          const { index } = elements[0];
                          const label = areaChartData.labels[index];
                          setShowViewDetails(true);
                          setOptionsDetail({
                            option: 'userArea',
                            value: label,
                            isFirstSection: true,
                          });
                        },
                      }}
                    />
                  </Card>
                </Col>
                <Col span={12}>
                  <Card title="Contratos por Mes">
                    <Bar data={monthChartData} options={stateChartOptions} />
                  </Card>
                </Col>
              </Row>
            </div>
            <hr />
            <div style={{ marginBottom: '20px' }}>
              <Select
                defaultValue="Todos"
                onChange={setSelectedResposable}
                style={{ width: 200, marginRight: 10 }}
              >
                <Option value="Todos">Todos(Responsable)</Option>
                {listLegalOwner.map((item) => (
                  <Option value={item.value} key={item.value}>
                    {item.label}
                  </Option>
                ))}
              </Select>
              <Select
                defaultValue=""
                onChange={setSelectedWeek}
                style={{ width: 200, marginRight: 10 }}
              >
                <Option value="">Todas las semanas</Option>
                {listWeeks.map((item) => (
                  <Option value={item.value} key={item.value}>
                    {item.label}
                  </Option>
                ))}
              </Select>
            </div>
            <div className="container-graphics">
              <Row gutter={16} style={{ marginTop: '20px' }}>
                <Col span={12}>
                  <Card title="Contratos Finalizados Próximos a Vencer">
                    <Pie
                      data={upcomingContractsData}
                      options={{
                        ...stateChartOptions,
                        onClick: (event, elements) => {
                          if (elements.length === 0) return;
                          const { index } = elements[0];
                          const label = upcomingContractsData.labels[index];
                          setShowViewDetails(true);
                          setOptionsDetail({
                            option: '',
                            value: label,
                            isFirstSection: false,
                          });
                        },
                      }}
                    />
                  </Card>
                </Col>
                <Col span={12}>
                  <Card title="Contratos por Área Solicitante">
                    <Bar
                      data={dataChart}
                      options={{
                        ...optionsChart,

                        onClick: (event, elements) => {
                          if (!elements.length) return;

                          const element = elements[0];

                          const { datasetIndex } = element;
                          const { index } = element;

                          const dataset = dataChart.datasets[datasetIndex];
                          const label = dataChart.labels[index]; // Área (eje X)
                          const state = dataset.label; // Vencido / Por vencer / Vigente
                          setShowViewDetails(true);
                          setOptionsDetail({
                            option: label,
                            value: state,
                            isFirstSection: false,
                          });
                        },
                      }}
                    />
                  </Card>
                </Col>
              </Row>
            </div>
            <div className="container-table-sumary">
              <Text className="subtitle">Resumen de las últimas solicitudes</Text>
              <Row gutter={16} style={{ marginTop: '20px' }}>
                <Col span={24}>
                  <Table dataSource={requests?.slice(0, 5)} columns={columnsTable} rowKey="id" />
                </Col>
              </Row>
              <Text className="link" onClick={goRequestList}>
                Ver todos
              </Text>
            </div>
          </div>
        </>
      ) : (
        <div>
          <Button style={{ marginBottom: 20 }} onClick={() => setShowViewDetails(false)}>
            volver
          </Button>
          <div className="container-table-sumary">
            <Text className="subtitle">Detall de los contratos</Text>
            <Row gutter={16} style={{ marginTop: '20px' }}>
              <Col span={24}>
                <Table
                  // eslint-disable-next-line no-undef
                  dataSource={filterByTipeAndValue(
                    optionsDetail.isFirstSection
                      ? [...filteredContracts]
                      : [...filteredContractsFinalized],
                    optionsDetail.option,
                    optionsDetail.value,
                    optionsDetail.isFirstSection
                  )}
                  columns={columnsTable}
                  rowKey="id"
                />
              </Col>
            </Row>
            <Text className="link" onClick={goRequestList}>
              Ver todos
            </Text>
          </div>
        </div>
      )}
    </div>
  );
}
